import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Check, Pencil, Percent, Plus, ShieldCheck, Trash2, X, IdCard } from "lucide-react";
import { CustomerDiscountDialog } from "@/components/CustomerDiscountDialog";
import { CustomerDetailsDialog } from "@/components/CustomerDetailsDialog";
import { GstVerify } from "@/components/GstVerify";
import { SupplierDetailsDialog } from "@/components/SupplierDetailsDialog";

export const MASTER_META = {
  quality: { type: "quality", title: "Quality Master", singular: "Quality", placeholder: "e.g. TR Suiting" },
  company: { type: "company", title: "Company Master", singular: "Company", placeholder: "e.g. CADINI SIYARAM" },
  "main-unit": { type: "main_unit", title: "Main Unit Master", singular: "Main Unit", placeholder: "e.g. Than" },
  "alt-unit": { type: "alt_unit", title: "Alternative Unit Master", singular: "Alternative Unit", placeholder: "e.g. Meter" },
  centre: { type: "centre", title: "Material Centre Master", singular: "Material Centre", placeholder: "e.g. MAIN GODOWN" },
  supplier: { type: "supplier", title: "Supplier Master", singular: "Supplier", placeholder: "e.g. SIYARAM SILK MILLS" },
  customer: { type: "customer", title: "Customer Master", singular: "Customer", placeholder: "e.g. SHREE CLOTH HOUSE" },
  "expense-head": { type: "expense_head", title: "Expense Head Master", singular: "Expense Head", placeholder: "e.g. PACKING & FORWARDING" },
};

export default function MasterPage() {
  const { masterKey } = useParams();
  const meta = MASTER_META[masterKey] || MASTER_META.quality;
  const [rows, setRows] = useState([]);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [discountRow, setDiscountRow] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [params, setParams] = useSearchParams();
  const [gstin, setGstin] = useState("");
  const [gstDetails, setGstDetails] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (params.get("focus") === "1") {
      nameRef.current?.focus();
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/masters/${meta.type}`);
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setValue("");
    load();
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterKey]);

  const add = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    try {
      await api.post("/masters", { type: meta.type, name: value.trim() });
      if (gstDetails && (meta.type === "customer" || meta.type === "supplier")) {
        await api.post("/masters/gst", {
          type: meta.type,
          name: value.trim(),
          gstin: gstDetails.gstin,
          legal_name: gstDetails.legal_name,
          trade_name: gstDetails.trade_name,
          address: gstDetails.address,
          city: gstDetails.city,
          state: gstDetails.state,
          pincode: gstDetails.pincode,
          gst_status: gstDetails.status,
          gst_verified_at: new Date().toISOString(),
        });
      }
      setValue("");
      setGstin("");
      setGstDetails(null);
      load();
      toast.success(`${meta.singular} added`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveEdit = async (row) => {
    const name = editValue.trim();
    if (!name || name === row.name) {
      setEditId(null);
      return;
    }
    try {
      await api.put(`/masters/${row.id}`, { name });
      setEditId(null);
      load();
      toast.success(`${meta.singular} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/masters/${id}`);
      load();
      toast.success(`${meta.singular} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const verifyAll = async () => {
    setSweeping(true);
    setSweep(null);
    try {
      const { data } = await api.post("/gst/verify-all");
      setSweep(data);
      toast[data.flagged.length ? "warning" : "success"](
        `${data.checked} GSTIN checked · ${data.active} active · ${data.flagged.length} need attention`
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSweeping(false);
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Master data</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">{meta.title}</h1>
        </div>

        {(meta.type === "customer" || meta.type === "supplier") && (
          <div className="mt-6 border border-zinc-200 bg-zinc-50 p-4" data-testid="master-gst-block">
            <GstVerify
              testId="master"
              value={gstin}
              onChange={(v) => { setGstin(v); setGstDetails(null); }}
              onVerified={(d) => {
                setGstDetails(d);
                if (d.trade_name || d.legal_name) setValue((d.trade_name || d.legal_name).toUpperCase());
              }}
            />
            {gstDetails && (
              <div className="mt-3 space-y-0.5 text-xs text-zinc-600" data-testid="master-gst-details">
                {gstDetails.legal_name && <p><b>Legal:</b> {gstDetails.legal_name}</p>}
                {gstDetails.trade_name && <p><b>Trade:</b> {gstDetails.trade_name}</p>}
                {gstDetails.address && <p><b>Address:</b> {gstDetails.address}</p>}
                <p><b>State:</b> {gstDetails.state || "—"}{gstDetails.status ? ` · ${gstDetails.status}` : ""}</p>
                {gstDetails.duplicate && (
                  <p className="pt-1 text-[11px] font-bold text-rose-600" data-testid="master-gst-duplicate">
                    Already saved on {gstDetails.duplicate.type} “{gstDetails.duplicate.name}”
                  </p>
                )}
                <p className="pt-1 text-[11px] text-zinc-500">
                  Verified details will be saved against this {meta.singular.toLowerCase()} when you press Add.
                </p>
              </div>
            )}
          </div>
        )}

        {(meta.type === "customer" || meta.type === "supplier") && (
          <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="master-sweep-block">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={sweeping}
              data-testid="master-verify-all-btn"
              onClick={verifyAll}
              className="rounded-sm"
            >
              <ShieldCheck size={14} className="mr-1.5" />
              {sweeping ? "Checking all saved GSTINs…" : "Verify all saved GSTINs"}
            </Button>
            {sweep && (
              <span className="text-xs text-zinc-600" data-testid="master-sweep-summary">
                {sweep.checked} checked · {sweep.active} active · {sweep.flagged.length} flagged
              </span>
            )}
          </div>
        )}

        {sweep?.flagged?.length > 0 && (
          <div className="mt-3 border border-rose-200 bg-rose-50 p-3" data-testid="master-sweep-flagged">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">Need attention</p>
            <div className="mt-2 divide-y divide-rose-100">
              {sweep.flagged.map((f) => (
                <div key={`${f.type}-${f.name}`} className="flex items-center justify-between gap-3 py-1.5 text-xs">
                  <span className="font-semibold">{f.name}</span>
                  <span className="mono text-[11px] text-zinc-600">{f.gstin}</span>
                  <span className="font-bold text-rose-700">{f.problem}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={add} className="flex gap-2 py-6">
          <Input
            data-testid="master-name-input"
            ref={nameRef}
            className="rounded-sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={meta.placeholder}
          />
          <Button data-testid="master-add-button" data-f2-save type="submit" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90">
            <Plus size={15} className="mr-2" /> Add
          </Button>
        </form>

        {loading ? (
          <p className="py-12 text-center text-sm text-zinc-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div data-testid="master-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Nothing added yet</h3>
            <p className="mt-1 text-sm text-zinc-500">
              Names added here appear as suggestions in Item Master.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 border border-zinc-200">
            {rows.map((row, idx) => (
              <motion.div
                key={row.id}
                data-testid={`master-row-${row.id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.2) }}
                className="flex items-center justify-between px-4 py-2.5"
              >
                {editId === row.id ? (
                  <Input
                    data-testid={`master-edit-input-${row.id}`}
                    autoFocus
                    className="mr-3 h-9 rounded-sm"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(row);
                      if (e.key === "Escape") setEditId(null);
                    }}
                  />
                ) : (
                <span className="text-sm">
                  {row.name}
                  {meta.type === "customer" && row.company_discounts?.length > 0 && (
                    <span className="ml-2 text-xs text-zinc-500">
                      {row.company_discounts.length} company rate(s)
                    </span>
                  )}
                </span>
                )}
                <div className="flex items-center gap-3">
                {editId === row.id ? (
                  <>
                    <button
                      type="button"
                      data-testid={`master-edit-save-${row.id}`}
                      onClick={() => saveEdit(row)}
                      className="text-zinc-500 transition-colors duration-200 hover:text-emerald-600"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      data-testid={`master-edit-cancel-${row.id}`}
                      onClick={() => setEditId(null)}
                      className="text-zinc-400 transition-colors duration-200 hover:text-zinc-950"
                    >
                      <X size={15} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid={`master-edit-${row.id}`}
                      onClick={() => {
                        setEditId(row.id);
                        setEditValue(row.name);
                      }}
                      className="text-zinc-400 transition-colors duration-200 hover:text-zinc-950"
                    >
                      <Pencil size={14} />
                    </button>
                  </>
                )}
                {(meta.type === "customer" || meta.type === "supplier") && (
                  <Button
                    data-testid={`master-details-${row.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setDetailsRow(row)}
                  >
                    <IdCard size={13} className="mr-1.5" /> Details
                  </Button>
                )}
                {meta.type === "customer" && (
                  <Button
                    data-testid={`master-discount-${row.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setDiscountRow(row)}
                  >
                    <Percent size={13} className="mr-1.5" /> Company discount
                  </Button>
                )}
                <button
                  type="button"
                  data-testid={`master-delete-${row.id}`}
                  onClick={() => remove(row.id)}
                  className="text-zinc-400 transition-colors duration-200 hover:text-red-600"
                >
                  <Trash2 size={14} />
                </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <CustomerDetailsDialog
        customer={detailsRow}
        open={!!detailsRow && meta.type === "customer"}
        onOpenChange={(o) => !o && setDetailsRow(null)}
        onSaved={load}
      />

      <SupplierDetailsDialog
        supplier={detailsRow}
        open={!!detailsRow && meta.type === "supplier"}
        onOpenChange={(o) => !o && setDetailsRow(null)}
        onSaved={load}
      />

      <CustomerDiscountDialog
        customer={discountRow}
        open={!!discountRow}
        onOpenChange={(o) => !o && setDiscountRow(null)}
        onSaved={load}
      />
    </AppShell>
  );
}
